import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

export const orgRecipientContext = createContext();

// Wrap the hook with a provider
export const ProvideOrgRecipient = ({ children }) => {
  const inc = useProvideOrgRecipient();
  // We are providing the object returned by useProvideOrgRecipient as the value of the orgRecipientContext
  return <orgRecipientContext.Provider value={inc}>{children}</orgRecipientContext.Provider>;
};

ProvideOrgRecipient.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useOrgRecipient = () => {
  // The context object will contain anything returned by useProvideOrgRecipient
  const context = useContext(orgRecipientContext);
  if (context === undefined) {
    throw new Error('useOrgRecipient must be used within a ProvideRecipient');
  }
  return context;
};

const initialState = {
  orgRecipient: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
  orgRecipients: [],
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_ORG_RECIPIENT: 'SET_ORG_RECIPIENT',
  UPDATE_ORG_RECIPIENT: 'UPDATE_ORG_RECIPIENT',
  DELETE_ORG_RECIPIENT: 'DELETE_ORG_RECIPIENT',
  SET_ID: 'SET_ID',
  DELETE: 'DELETE',
  RESET: 'RESET',
});


const orgRecipientReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Organization Recipient has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        orgRecipient: action.payload,
        orgRecipients: action.payload?.orgRecipients || [],
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        orgRecipient: null,
        stale: false,
        error: 'Organization Recipient not found',
      };
    case ACTIONS.UPDATE_ORG_RECIPIENT:
    case ACTIONS.DELETE_ORG_RECIPIENT:
      return {
        ...nextState,
        // orgRecipient: action.payload,
        version: action.payload.version,
        stale: true,
      };
    case ACTIONS.DELETED:
      return {
        ...nextState,
        orgRecipient: null,
        stale: true,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      // let stale = nextState.id === action.payload ? false : true;// TODO: not sure what this is doing so hard coding for now
      let stale = true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_ORG_RECIPIENT:
      return {
        ...nextState,
        orgRecipients: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`orgRecipientReducer this action is not supported: ${action.type}`);
  }
};

const useProvideOrgRecipient = () => {
  const controller = useRef(null);
  //orgRecipient state as a reducer
  const [orgRecipientState, dispatch] = useReducer(orgRecipientReducer, initialState);

  useEffect(() => {
    const {id, stale} = orgRecipientState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getOrgRecipient = () => {
        axios.get(`/api/orgrecipient/get?id=${id}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_ERROR, payload: error});
          }
        });
      }

      getOrgRecipient();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
  }, [orgRecipientState.stale, orgRecipientState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (orgRecipientState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    orgRecipientState,
  }
};
